import React, { useState } from 'react';
import styled from 'astroturf/react';
import { Body as BodyBase } from 'js/design_system/Body';
import { Menu } from 'js/design_system/Menu';
import { Icon } from 'js/design_system/Icon';
import LikedIcon from 'js/design_system/icons/thumb_up_filled.svg';
import CommentIcon from 'js/design_system/icons/chat_bubble_outline.svg';
import RepostIcon from 'js/design_system/icons/repeat.svg';
import RepostedIcon from 'js/design_system/icons/done.svg';
import { Button } from 'js/social/shared/design_system/Button';
import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  margin-left: -8px;
`;

const CtaContainer = styled.div`
  flex-grow: 1;
  display: flex;
  gap: 4px;
`;

const RepostDropdownItem = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2px;
  background: none;
  border: none;
  cursor: pointer;
  margin: 0;
  padding: 6px 8px;

  &:hover {
    background-color: $ds-off-white;
  }
`;

const RepostCta = styled(BodyBase)`
  color: $ds-gray-dark;
  text-align: left;
  font-weight: 500;
`;

const RepostDescription = styled(BodyBase)`
  color: $ds-gray-dark;
  text-align: left;
`;

interface Props {
  className?: string;
  onCommentClick: () => void;
  onQuoteClick?: () => void;
  like: {
    liked: boolean;
    likeNews: () => Promise<void>;
    unlikeNews: () => Promise<void>;
  };
  repost?: {
    reposted: boolean;
    repostNews: () => Promise<void>;
    unrepostNews: () => Promise<void>;
  };
  hideLikeAction?: boolean;
  hideCommentAction?: boolean;
  hideRepostAction?: boolean;
  impressionProps: ImpressionProps;
}

export const Cta: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  like: { liked, likeNews, unlikeNews },
  repost,
  onCommentClick,
  onQuoteClick,
  children,
  hideLikeAction = false,
  hideCommentAction = false,
  hideRepostAction = false,
  impressionProps,
}) => {
  const [disableLike, setDisableLike] = useState(false);
  const [disableRepost, setDisableRepost] = useState(false);

  const onLikeClicked = () => {
    setDisableLike(true);
    likeNews().finally(() => setDisableLike(false));
  };
  const onUnlikeClicked = () => {
    setDisableLike(true);
    unlikeNews().finally(() => setDisableLike(false));
  };

  const onRepostClicked = () => {
    if (repost == null) return;
    setDisableRepost(true);
    repost.repostNews().finally(() => setDisableRepost(false));
  };
  const onUnrepostClicked = () => {
    if (repost == null) return;
    setDisableRepost(true);
    repost.unrepostNews().finally(() => setDisableRepost(false));
  };

  const repostDropdownItems: React.ReactNode[] = [
    onQuoteClick && (
      <RepostDropdownItem key="quote" onClick={onQuoteClick}>
        <RepostCta size="md">Repost with your thoughts</RepostCta>
        <RepostDescription size="sm">
          Share this post and your thoughts about it
        </RepostDescription>
      </RepostDropdownItem>
    ),
    repost && (
      <RepostDropdownItem
        key="repost"
        onClick={repost.reposted ? onUnrepostClicked : onRepostClicked}
        disabled={disableRepost}
      >
        {repost?.reposted ? (
          <RepostCta size="md">Delete Repost</RepostCta>
        ) : (
          <>
            <RepostCta size="md">Repost</RepostCta>
            <RepostDescription size="sm">
              Instantly share this post with others
            </RepostDescription>
          </>
        )}
      </RepostDropdownItem>
    ),
  ].filter(Boolean);

  const { source } = impressionProps;

  return (
    <Container className={className}>
      <CtaContainer>
        <>
          {!hideLikeAction && (
            <ImpressionWatcher
              {...impressionProps}
              source={`${source}_like-button`}
            >
              <Button
                variant="text"
                icon={
                  liked ? (
                    <LikedIcon size={20} />
                  ) : (
                    <Icon name="thumb_up" size={20} />
                  )
                }
                iconOnlyBreakpoint="sm"
                disabled={disableLike}
                onClick={liked ? onUnlikeClicked : onLikeClicked}
              >
                {liked ? 'Liked' : 'Like'}
              </Button>
            </ImpressionWatcher>
          )}

          {!hideCommentAction && (
            <ImpressionWatcher
              {...impressionProps}
              source={`${source}_comment-button`}
            >
              <Button
                variant="text"
                icon={<CommentIcon size={20} />}
                iconOnlyBreakpoint="sm"
                onClick={onCommentClick}
              >
                Comment
              </Button>
            </ImpressionWatcher>
          )}

          {!hideRepostAction && repostDropdownItems.length > 0 && (
            <Menu placement="bottom-start" render={() => repostDropdownItems}>
              <ImpressionWatcher
                {...impressionProps}
                source={`${source}_repost-button`}
              >
                <Button
                  variant="text"
                  icon={
                    repost?.reposted ? (
                      <RepostedIcon size={20} />
                    ) : (
                      <RepostIcon size={20} />
                    )
                  }
                  iconOnlyBreakpoint="sm"
                >
                  {repost?.reposted ? 'Reposted' : 'Repost'}
                </Button>
              </ImpressionWatcher>
            </Menu>
          )}
        </>
      </CtaContainer>
      {children}
    </Container>
  );
};
